import React,{useState,useEffect} from 'react';
import { FiRefreshCw, FiCalendar, FiZap, FiCheckCircle, FiAlertTriangle,FiEdit2 ,FiLayers, FiSettings, FiImage, FiLink } from 'react-icons/fi';
import { FaShopify } from 'react-icons/fa';

export default function AIFixesDashboard1(){
    // State for active tab and mock data
    const [activeTab, setActiveTab] = useState('meta');
    const [selectedItems, setSelectedItems] = useState([]);
    const [fixHistory, setFixHistory] = useState([]);
    
    // Mock data - in a real app, this would come from API
    const [issues, setIssues] = useState({
      meta: [
        {
          id: 1,
          page: '/products/blue-shoes',
          currentTitle: 'Blue Shoes',
          currentDesc: '',
          suggestedTitle: 'Comfortable Blue Running Shoes for Men – 2024 Collection',
          suggestedDesc: 'Discover our premium blue running shoes designed for maximum comfort and performance. Perfect for athletes and casual wear.',
          status: 'critical'
        },
        // More items...
      ],
      altText: [
        {
          id: 101,
          imageUrl: 'blue-shoes.jpg',
          currentAlt: '',
          suggestedAlt: 'Blue running shoes on wooden floor',
          productContext: 'Nike Air Max Running Shoes',
          status: 'warning'
        },
        // More items...
      ],
      links: [
        {
          id: 201,
          url: '/old-collection',
          sourcePage: '/summer-sale',
          status: '404',
          suggestion: '/new-collection',
          action: 'redirect'
        },
        // More items...
      ],
      content: [
        {
          id: 301,
          page: '/products/wireless-headphones',
          issue: 'Keyword density low for "wireless headphones"',
          suggestion: 'Add keyword 2-3 times in product description'
        },
        // More items...
      ]
    });
  
    // Apply single fix
    const applyFix = (type, id) => {
      // In a real app, this would call your API
      console.log(`Applying fix for ${type} item ${id}`);
      
      // Add to history for undo functionality
      setFixHistory([...fixHistory, { type, id, action: 'apply' }]);
      
      // Remove from issues list (simulate successful fix)
      setIssues(prev => ({
        ...prev,
        [type]: prev[type].filter(item => item.id !== id)
      }));
    };
  
    // Bulk apply fixes
    const applyBulkFixes = (type) => {
      const ids = issues[type].map(item => item.id);
      ids.forEach(id => applyFix(type, id));
    };
  
    // Undo last action
    const undoLastAction = () => {
      if (fixHistory.length === 0) return;
      
      const lastAction = fixHistory[fixHistory.length - 1];
      // In a real app, you would revert via API
      console.log(`Undoing ${lastAction.type} fix for item ${lastAction.id}`);
      
      setFixHistory(fixHistory.slice(0, -1));
    };
  
    // Toggle item selection
    const toggleSelection = (type, id) => {
      const itemKey = `${type}-${id}`;
      setSelectedItems(prev => 
        prev.includes(itemKey) 
          ? prev.filter(item => item !== itemKey) 
          : [...prev, itemKey]
      );
    };
  
    // Calculate fixable issues count
    const fixableCount = Object.values(issues).reduce((sum, arr) => sum + arr.length, 0);
  
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-rich_black-500 text-white shadow-md">
          <div className="container mx-auto px-6 py-4 flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <FaShopify className="text-tiffany_blue-500 text-2xl" />
              <span className="text-xl font-bold">SEOAutoFix</span>
            </div>
            <nav className="hidden md:flex space-x-6">
              <a href="#" className="hover:text-tiffany_blue-300 transition">Dashboard</a>
              <a href="#" className="hover:text-tiffany_blue-300 transition">Reports</a>
              <a href="#" className="hover:text-tiffany_blue-300 transition">Settings</a>
            </nav>
            <button className="bg-midnight_green-500 hover:bg-midnight_green-600 px-4 py-2 rounded-md transition">
              My Store
            </button>
          </div>
        </header>
  
        {/* Main Content */}
        <main className="container mx-auto px-6 py-8">
          {/* Quick Fixes Overview Bar */}
          <div 
            className="bg-white rounded-xl shadow-md p-6 mb-8 border-l-4 border-rust-500"
            data-aos="fade-up"
          >
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div className="mb-4 md:mb-0">
                <h2 className="text-2xl font-bold text-rich_black-800 mb-2">AI-Powered Fixes</h2>
                <div className="flex items-center">
                  <div className="w-64 bg-gray-200 rounded-full h-2.5 mr-4">
                    <div 
                      className="bg-gradient-to-r from-dark_cyan-500 to-tiffany_blue-500 h-2.5 rounded-full" 
                      style={{ width: `${(fixableCount / 24) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-rich_black-700">
                    {fixableCount} of 24 issues fixable automatically
                  </span>
                </div>
              </div>
              <div className="text-center md:text-right">
                <p className="text-rich_black-600 mb-2">
                  Estimated SEO score impact: <span className="font-bold">65 → 89</span>
                </p>
                <button 
                  onClick={() => applyBulkFixes(activeTab)}
                  className="bg-gradient-to-r from-dark_cyan-500 to-tiffany_blue-500 text-white px-6 py-3 rounded-md hover:opacity-90 transition shadow-md font-medium"
                >
                  Fix All Now
                </button>
              </div>
            </div>
          </div>
  
          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
            {[
              { id: 'meta', icon: <FiEdit2 className="mr-2" />, label: 'Meta Tags', count: issues.meta.length },
              { id: 'altText', icon: <FiImage className="mr-2" />, label: 'Alt Text', count: issues.altText.length },
              { id: 'links', icon: <FiLink className="mr-2" />, label: 'Broken Links', count: issues.links.length },
              { id: 'content', icon: <FiLayers className="mr-2" />, label: 'Content', count: issues.content.length }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-4 py-3 font-medium text-sm border-b-2 transition ${activeTab === tab.id ? 'border-dark_cyan-500 text-dark_cyan-600' : 'border-transparent text-rich_black-500 hover:text-rich_black-700'}`}
              >
                {tab.icon}
                {tab.label}
                {tab.count > 0 && (
                  <span className={`ml-2 px-2 py-1 text-xs rounded-full ${activeTab === tab.id ? 'bg-dark_cyan-100 text-dark_cyan-700' : 'bg-gray-100 text-rich_black-600'}`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
  
          {/* Tab Content */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            {/* Meta Tags Tab */}
            {activeTab === 'meta' && (
              <div className="divide-y divide-gray-200">
                {issues.meta.map(item => (
                  <div key={item.id} className="p-6 hover:bg-gray-50 transition">
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(`meta-${item.id}`)}
                        onChange={() => toggleSelection('meta', item.id)}
                        className="mt-1 mr-4"
                      />
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <span className="font-medium text-rich_black-800">{item.page}</span>
                          {item.status === 'critical' && (
                            <span className="ml-2 px-2 py-1 text-xs rounded-full bg-rufous-100 text-rufous-700 flex items-center">
                              <FiAlertTriangle className="mr-1" /> Critical
                            </span>
                          )}
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-6 mb-4">
                          <div>
                            <h4 className="text-sm font-medium text-rich_black-600 mb-2">Current</h4>
                            <div className="bg-gray-50 p-3 rounded-md">
                              {item.currentTitle ? (
                                <p className="text-rich_black-700">{item.currentTitle}</p>
                              ) : (
                                <p className="text-gray-400 italic">Missing title</p>
                              )}
                              {item.currentDesc ? (
                                <p className="text-rich_black-500 text-sm mt-1">{item.currentDesc}</p>
                              ) : (
                                <p className="text-gray-400 italic text-sm mt-1">Missing description</p>
                              )}
                            </div>
                          </div>
                          
                          <div>
                            <h4 className="text-sm font-medium text-rich_black-600 mb-2">AI Suggestion</h4>
                            <div className="bg-tiffany_blue-50 p-3 rounded-md border border-tiffany_blue-100">
                              <p className="text-rich_black-800">{item.suggestedTitle}</p>
                              <p className="text-rich_black-600 text-sm mt-1">{item.suggestedDesc}</p>
                              <div className="flex items-center mt-2 text-xs text-rich_black-500">
                                <span className={`mr-3 ${item.suggestedTitle.length > 60 ? 'text-rufous-500' : 'text-dark_cyan-500'}`}>
                                  Title: {item.suggestedTitle.length}/60
                                </span>
                                <span className={item.suggestedDesc.length > 155 ? 'text-rufous-500' : 'text-dark_cyan-500'}>
                                  Desc: {item.suggestedDesc.length}/155
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap items-center justify-between">
                          <div className="mb-2 md:mb-0">
                            <label className="text-sm text-rich_black-600 mr-2">Tone:</label>
                            <select className="text-sm border border-gray-300 rounded px-2 py-1 bg-white">
                              <option>Professional</option>
                              <option>Friendly</option>
                              <option>Urgent</option>
                              <option>Informative</option>
                            </select>
                            <label className="ml-4 text-sm text-rich_black-600 flex items-center">
                              <input type="checkbox" className="mr-2" checked /> 
                              Include Primary Keyword
                            </label>
                          </div>
                          
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => applyFix('meta', item.id)}
                              className="bg-dark_cyan-500 hover:bg-dark_cyan-600 text-white px-4 py-2 rounded-md text-sm font-medium transition"
                            >
                              Apply
                            </button>
                            <button 
                              onClick={() => console.log('Regenerate suggestion for', item.id)}
                              className="flex items-center bg-white border border-gray-300 text-rich_black-600 hover:bg-gray-50 px-4 py-2 rounded-md text-sm font-medium transition"
                            >
                              <FiRefreshCw className="mr-1" /> Regenerate
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Alt Text Tab */}
            {activeTab === 'altText' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
                {issues.altText.map(item => (
                  <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition">
                    <div className="relative group">
                      <img 
                        src={`https://placehold.co/400x300/eee/aaa?text=Product+Image`} 
                        alt={item.currentAlt || ''}
                        className="w-full h-48 object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-rich_black-800/70 to-transparent opacity-0 group-hover:opacity-100 transition flex items-end p-4">
                        <span className="text-white text-sm">{item.productContext}</span>
                      </div>
                    </div>
                    
                    <div className="p-4">
                      <div className="flex items-start mb-3">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(`altText-${item.id}`)}
                          onChange={() => toggleSelection('altText', item.id)}
                          className="mt-1 mr-3"
                        />
                        <div className="flex-1">
                          <h4 className="font-medium text-rich_black-800 mb-1">Suggested Alt Text</h4>
                          <div className="bg-tiffany_blue-50 p-2 rounded border border-tiffany_blue-100 mb-3">
                            <p className="text-rich_black-700">{item.suggestedAlt}</p>
                          </div>
                          <div className="flex justify-between">
                            <button 
                              onClick={() => console.log('Edit suggestion for', item.id)}
                              className="text-dark_cyan-600 hover:text-dark_cyan-800 text-sm font-medium flex items-center"
                            >
                              <FiEdit2 className="mr-1" /> Edit
                            </button>
                            <button 
                              onClick={() => applyFix('altText', item.id)}
                              className="bg-dark_cyan-500 hover:bg-dark_cyan-600 text-white px-3 py-1 rounded-md text-sm font-medium transition"
                            >
                              Apply
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Broken Links Tab */}
            {activeTab === 'links' && (
              <div className="divide-y divide-gray-200">
                {issues.links.map(item => (
                  <div key={item.id} className="p-6 hover:bg-gray-50 transition">
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(`links-${item.id}`)}
                        onChange={() => toggleSelection('links', item.id)}
                        className="mt-1 mr-4"
                      />
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center justify-between mb-3">
                          <div>
                            <h4 className="font-medium text-rich_black-800 mb-1">Broken Link</h4>
                            <p className="text-rich_black-600 text-sm">{item.url}</p>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            item.status === '404' ? 'bg-rufous-100 text-rufous-700' : 'bg-alloy_orange-100 text-alloy_orange-700'
                          }`}>
                            {item.status}
                          </span>
                        </div>
                        
                        <div className="bg-gray-50 p-4 rounded-md mb-4">
                          <div className="text-sm text-rich_black-600 mb-2">
                            Found on: <span className="font-medium">{item.sourcePage}</span>
                          </div>
                          
                          <div className="flex items-center text-sm mb-2">
                            <FiInfo className="text-dark_cyan-500 mr-2" />
                            <span className="text-rich_black-700">AI Suggestion:</span>
                          </div>
                          
                          <div className="ml-6">
                            {item.action === 'redirect' ? (
                              <div className="flex items-center">
                                <span className="text-rich_black-600 mr-2">Redirect to:</span>
                                <span className="font-medium text-dark_cyan-600">{item.suggestion}</span>
                              </div>
                            ) : (
                              <span className="text-rich_black-600">Remove this link</span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                          <button 
                            onClick={() => applyFix('links', item.id)}
                            className="bg-dark_cyan-500 hover:bg-dark_cyan-600 text-white px-4 py-2 rounded-md text-sm font-medium transition"
                          >
                            {item.action === 'redirect' ? 'Create Redirect' : 'Remove Link'}
                          </button>
                          <button 
                            onClick={() => console.log('Alternative action for', item.id)}
                            className="bg-white border border-gray-300 text-rich_black-600 hover:bg-gray-50 px-4 py-2 rounded-md text-sm font-medium transition"
                          >
                            Alternative Action
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Content Optimization Tab */}
            {activeTab === 'content' && (
              <div className="divide-y divide-gray-200">
                {issues.content.map(item => (
                  <div key={item.id} className="p-6 hover:bg-gray-50 transition">
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(`content-${item.id}`)}
                        onChange={() => toggleSelection('content', item.id)}
                        className="mt-1 mr-4"
                      />
                      <div className="flex-1">
                        <div className="mb-3">
                          <h4 className="font-medium text-rich_black-800">{item.page}</h4>
                          <p className="text-rich_black-600 text-sm">{item.issue}</p>
                        </div>
                        
                        <div className="bg-tiffany_blue-50 p-4 rounded-md border border-tiffany_blue-100 mb-4">
                          <h5 className="font-medium text-dark_cyan-700 mb-2">AI Recommendation</h5>
                          <p className="text-rich_black-700">{item.suggestion}</p>
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                          <button 
                            onClick={() => console.log('Apply content fix for', item.id)}
                            className="bg-dark_cyan-500 hover:bg-dark_cyan-600 text-white px-4 py-2 rounded-md text-sm font-medium transition"
                          >
                            Apply Fix
                          </button>
                          <button 
                            onClick={() => console.log('Rewrite content for', item.id)}
                            className="flex items-center bg-white border border-gray-300 text-rich_black-600 hover:bg-gray-50 px-4 py-2 rounded-md text-sm font-medium transition"
                          >
                            <FiEdit2 className="mr-1" /> Rewrite Content
                          </button>
                          <button 
                            onClick={() => console.log('Generate blog ideas for', item.id)}
                            className="flex items-center bg-white border border-gray-300 text-rich_black-600 hover:bg-gray-50 px-4 py-2 rounded-md text-sm font-medium transition"
                          >
                            <FiLayers className="mr-1" /> Blog Ideas
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Bulk Actions Footer */}
          {selectedItems.length > 0 && (
            <div className="fixed bottom-0 left-0 right-0 bg-rich_black-800 text-white shadow-lg p-4">
              <div className="container mx-auto flex flex-col md:flex-row items-center justify-between">
                <div className="mb-2 md:mb-0">
                  <span className="font-medium">{selectedItems.length} items selected</span>
                </div>
                
                <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-4 w-full md:w-auto">
                  <select className="bg-rich_black-700 border border-rich_black-600 text-white rounded px-3 py-2 text-sm">
                    <option>Apply fix to all selected</option>
                    <option>Update meta tags</option>
                    <option>Add alt text</option>
                    <option>Fix broken links</option>
                  </select>
                  
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => setSelectedItems([])}
                      className="bg-rich_black-600 hover:bg-rich_black-700 px-4 py-2 rounded-md text-sm font-medium transition"
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={() => {
                        // Apply fixes to all selected items
                        console.log('Applying fixes to', selectedItems);
                        setSelectedItems([]);
                      }}
                      className="bg-dark_cyan-500 hover:bg-dark_cyan-600 px-4 py-2 rounded-md text-sm font-medium transition flex items-center"
                    >
                      <FiCheck className="mr-1" /> Apply to Selected
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    );
  };
  